import { asyncAddToCart } from '@/js/api/asyncAddToCart'
import { PERSONALIZATION_FIELD_KEYS } from '@/js/personalize-product/data/personalization-fields'
import { TOAST_TYPES } from '@/js/toast-messages/container/ToastMessages'

class VariantSelection extends HTMLElement {
    static ELEMENT_NAME = 'variant-selection'

    static CLASSES = {
        errorMessage: 'form__error-message'
    }

    $elements = {
        inputs: null,
        form: null,
        cartButton: null,
        submitButton: null,
        errorMessage: null,
    }

    productData = null;

    isAddingToCart = false;

    constructor() {
        super()

        // look for all elements that have a `name` attribute
        const inputs = this.querySelectorAll(`[data-variant-selection-input][name]`)

        // keep only the non-disabled ones
        this.$elements.inputs = Array.from(inputs).filter(input => !input.disabled);
        this.$elements.form = this.closest('form')
        this.$elements.submitButton = this.$elements.form.querySelector('button[type="submit"]')
        this.$elements.cartButton = document.querySelector('cart-button');

        this.renderErrorMessage();

        this.$elements.inputs.map(input => input.addEventListener('change', this.handleChange.bind(this)))

        this.$elements.form.addEventListener('submit', this.handleSubmit.bind(this))

    }

    /**
     * RENDERING
     */

    renderErrorMessage() {
        const errorMessage = document.createElement('div')
        errorMessage.classList.add(VariantSelection.CLASSES.errorMessage)
        errorMessage.hidden = true

        this.$elements.submitButton.insertAdjacentElement('afterend', errorMessage)
        this.$elements.errorMessage = this.$elements.form.querySelector(`.${VariantSelection.CLASSES.errorMessage}`)
    }

    /**
     * API
     */
    async addToCart(item, onSuccess) {
        this.isAddingToCart = true;

        const items = [];
        items.push(item)

        try {
            const response = await asyncAddToCart(items)

            if (response.ok) {
                onSuccess && onSuccess(response)
            }

            return response
        } catch (error) {
            throw new Error('Failed to add item to cart')
        } finally {
            this.isAddingToCart = false;
        }
    }

    /**
     * FUNCTIONS
     */

    disableSubmitButton() {
        this.$elements.submitButton.disabled = true;
    }

    enableSubmitButton() {
        this.$elements.submitButton.disabled = false;
    }

    showErrorMessage(message) {
        this.$elements.errorMessage.textContent = message;
        this.$elements.errorMessage.hidden = false;
    }

    hideErrorMessage() {
        this.$elements.errorMessage.textContent = '';
        this.$elements.errorMessage.hidden = true;
    }

    async submitAddToCart(item, onSuccess) {
        this.disableSubmitButton();
        this.hideErrorMessage();

        const response = await this.addToCart(item, onSuccess)

        if (response.ok) {
            // all good
        } else {
            // 422 may mean: maximum allowed quantity reached, product is out of stock, etc.
            if (response.status && response.status === 422) {
                this.showErrorMessage("Von diesem Produkt können keine weiteren in den Warenkorb gelegt werden.")

                this.enableSubmitButton();
            }
        }

        this.enableSubmitButton();
    }


    /**
     * EVENTS
     */

    /**
     * As new variant options are selected, the URL is updated to reflect the new selection.
     */
    handleChange() {
        this.enableSubmitButton();

        // first, from every input, get the id
        const values = new Set();

        this.$elements.inputs.forEach(input => {
            if (input.type === 'checkbox' && input.checked) {
                values.add(input.value);
            } else if (input.type === 'radio') {
                const checked = this.querySelector(`input[type="radio"][name="${input.name}"]:checked`);
                if (checked) values.add(checked.value);
            } else if (input.tagName === 'SELECT') {
                if (input.multiple) {
                    Array.from(input.selectedOptions).forEach(opt => values.add(opt.value));
                } else {
                    if (input.value) values.add(input.value);
                }
            } else {
                if (input.value) values.add(input.value);
            }
        });

        const queryString = `option_values=${[...values].join(',')}`;

        // get current params
        const currentParams = new URLSearchParams(window.location.search);

        // remove the old option_values
        currentParams.delete('option_values');

        // build new URL with updated option_values
        const newQueryString = `${queryString}${currentParams.toString() ? '&' + currentParams.toString() : ''}`;

        window.location.href = `${window.location.pathname}?${newQueryString}`;

    }

    /**
     * Submitting the form adds products to the cart. Instead of redirecting to the cart page, add it to the cart via ajax, upate the cart icon and send a notification
     */
    handleSubmit(e) {
        e.preventDefault()
        const formData = new FormData(this.$elements.form)
        const itemData = Object.fromEntries(formData.entries())
        const personalizationData = itemData['personalization-data'] ? JSON.parse(itemData['personalization-data']) : {}
        const quantity = parseInt(itemData.quantity || '1', 10);

        // delete this off the itemData object, as it's unnecessary for the cart API.'
        if (itemData['personalization-data']) {
            delete itemData['personalization-data']
        }

        let properties = {}

        // leave it empty if no text was set at all
        if (personalizationData[PERSONALIZATION_FIELD_KEYS.text] || ( personalizationData[PERSONALIZATION_FIELD_KEYS.text] && personalizationData[PERSONALIZATION_FIELD_KEYS.text].trim() !== '')) {
            // store the translated labels as keys and the translated values as values, so that they appear as human-readable in the cart.
            Object.entries(personalizationData).forEach(([key, value]) => {
                properties[value.label] = value.value
            })

            // this hard-coded property is used to determine whether the product is personalized or not.
            properties["_personalized"] = 1
        }

        // assert that when the URL contains personalization data, the user attempted to personalize the product
        const urlParams = new URLSearchParams(window.location.search)
        const personalizationDataFromUrl = urlParams.get(PERSONALIZATION_FIELD_KEYS.text);

        this.submitAddToCart( {...itemData, properties}, () => {
            this.$elements.cartButton.increaseCounterBy(quantity)

            if (personalizationDataFromUrl && !personalizationData[PERSONALIZATION_FIELD_KEYS.text]) {
                window.dispatchEvent(
                    new CustomEvent('toast', {
                        detail: {
                            message: `${quantity} Artikel wurde(n) dem Warenkorb hinzugefügt, jedoch konnten die Optionen zur Personalisierung aufgrund von Fehlern im Formular nicht gespeichert werden.`,
                            type: TOAST_TYPES.cartAdded
                        }
                    })
                )
            } else {
                window.dispatchEvent(
                    new CustomEvent('toast', {
                        detail: {
                            message: `${quantity} Artikel wurde(n) dem Warenkorb hinzugefügt!`,
                            type: TOAST_TYPES.cartAdded
                        }
                    })
                )
            }
        })
    }

    /**
     * FUNCTIONS
     */



    /**
     * LIFECYCLE
     */
    connectedCallback() {

    }
}

customElements.define(VariantSelection.ELEMENT_NAME, VariantSelection);